import type { ReactNode } from 'react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { Label } from '@/components/ui';
import { cn } from '@/lib/utils';
import { normalizeHex } from '@/lib/theme/theme-knobs';

interface ColorFieldProps {
  /** id for the hex text input (and the field's <label htmlFor>). */
  id: string;
  /** Visible field label. */
  label?: string;
  /** Current colour, canonical `#RRGGBB`. */
  value: string;
  /** Called with a normalised `#RRGGBB` whenever the colour changes. */
  onChange: (hex: string) => void;
  /** Read-only mode (e.g. for non-SERVICE_WRITE roles). */
  disabled?: boolean;
  className?: string;
  /** Extra controls rendered next to the hex input (e.g. pipette / EyeDropper buttons). */
  actions?: ReactNode;
}

/**
 * Reusable colour control (Spec 340): a react-colorful saturation/hue picker plus a
 * free-text hex input. Both emit a normalised canonical `#RRGGBB` via {@link normalizeHex}
 * — invalid input is never propagated. The picker is purely visual; the hex input and
 * the optional `actions` slot drive the same `onChange`.
 */
export function ColorField({
  id,
  label,
  value,
  onChange,
  disabled = false,
  className,
  actions,
}: ColorFieldProps) {
  const emit = (next: string) => {
    if (disabled) return;
    onChange(normalizeHex(next) ?? value);
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && <Label htmlFor={id}>{label}</Label>}

      <div
        className={cn('w-full max-w-[220px]', disabled && 'pointer-events-none opacity-50')}
        aria-disabled={disabled || undefined}
      >
        <HexColorPicker color={value} onChange={emit} />
      </div>

      <div className="flex items-center gap-2">
        <span
          className="inline-block h-8 w-8 shrink-0 rounded-md border border-input"
          style={{ backgroundColor: value }}
          aria-hidden="true"
        />
        <HexColorInput
          id={id}
          color={value}
          onChange={emit}
          prefixed
          disabled={disabled}
          className={cn(
            'flex h-10 w-28 rounded-md border border-input bg-background px-3 py-2 text-sm uppercase',
            'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          )}
        />
        {actions}
      </div>
    </div>
  );
}
